import type { GroupDimension, PayloadSnapshot, Row } from './types'
import { NO_CATEGORY } from './viewState'

export const NO_INITIATIVE = 'No initiative'
export const UNASSIGNED = 'Unassigned'

export function appUnit(row: Row, payload: Pick<PayloadSnapshot, 'app_multi' | 'app_none'>): string {
  return row.app || (row.app_n ? payload.app_multi : payload.app_none)
}

export function unitValue(row: Row, dimension: GroupDimension | 'manager', payload: Pick<PayloadSnapshot, 'no_manager_label' | 'app_multi' | 'app_none'>): string {
  switch (dimension) {
    case 'project': return row.project
    case 'cat': return row.cat || NO_CATEGORY
    case 'manager': return row.mgr || payload.no_manager_label
    case 'assignee': return row.assignee || UNASSIGNED
    case 'svp': return row.svp
    case 'epic': return row.epic || NO_INITIATIVE
    case 'app': return appUnit(row, payload)
  }
  return ''
}

export function groupKey(row: Row, dimension: GroupDimension): string {
  switch (dimension) {
    case 'project': return row.project
    case 'cat': return row.cat || NO_CATEGORY
    case 'epic': return `${row.epic_key || 'none'}||${row.epic || NO_INITIATIVE}`
    case 'assignee': return row.assignee || `\u007f${UNASSIGNED}`
    case 'svp': return row.svp
    case 'manager':
    case 'app':
      return unitValue(row, dimension, {
        no_manager_label: 'No manager mapped in Cortex',
        app_multi: 'Project covers several apps, not attributable to one',
        app_none: 'No app mapped in Cortex',
      })
  }
  return ''
}

export function areaLabel(value: string, payload: Pick<PayloadSnapshot, 'unowned_group' | 'svp_lead'>): string {
  if (value === payload.unowned_group) return value
  return payload.svp_lead[value] ? `${payload.svp_lead[value]} (${value})` : `${value} (no SVP owner)`
}

export function groupLabel(key: string, dimension: GroupDimension, payload: Pick<PayloadSnapshot, 'categories' | 'no_cat_label' | 'unowned_group' | 'svp_lead'>): string {
  switch (dimension) {
    case 'cat': return key === NO_CATEGORY ? payload.no_cat_label : payload.categories[key as keyof typeof payload.categories]
    // Callers pass either a groupKey() ("EPIC-1||Name") or a unitValue() (the bare name). Splitting
    // unconditionally returned undefined for the latter, which rendered blank cells and an
    // "...for undefined" aria-label in the accountability and monthly tables.
    case 'epic': return key.includes('||') ? key.slice(key.indexOf('||') + 2) : key
    case 'assignee': return key === `\u007f${UNASSIGNED}` || key === UNASSIGNED ? 'Unassigned (not yet pulled into active work)' : key
    case 'svp': return areaLabel(key, payload)
    default: return key
  }
}

export function isGapUnit(
  dimension: GroupDimension | 'manager',
  value: string,
  payload: Pick<PayloadSnapshot, 'no_manager_label' | 'app_multi' | 'app_none' | 'unowned_group'>,
): boolean {
  return (dimension === 'manager' && value === payload.no_manager_label)
    || (dimension === 'app' && (value === payload.app_multi || value === payload.app_none))
    || (dimension === 'svp' && value === payload.unowned_group)
    || (dimension === 'assignee' && value === UNASSIGNED)
}

export interface PairedGroup {
  rows: Row[]
  base: Row[]
}

export function pairGroups(rows: Row[], base: Row[], key: (row: Row) => string): Map<string, PairedGroup> {
  const groups = new Map<string, PairedGroup>()
  const add = (value: string): PairedGroup => {
    const existing = groups.get(value)
    if (existing) return existing
    const next = { rows: [], base: [] }
    groups.set(value, next)
    return next
  }
  base.forEach((row) => add(key(row)).base.push(row))
  rows.forEach((row) => add(key(row)).rows.push(row))
  return groups
}
